import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";
import { Button, ButtonProps } from "./button";

function ShadPopOver({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "bg-popover overflow-hidden text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[9999] rounded-[4px]  shadow-2xl outline-hidden",
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

type HeaderComponentProps = {
  title: string;
  postFix?: React.ReactNode;
};

export type PopOverProps = Omit<
  React.ComponentProps<typeof PopoverPrimitive.Root>,
  "children"
> &
  React.PropsWithChildren & {
    triggerMode?: "click" | "hover";
    hoverOpenDelay?: number;
    hoverCloseDelay?: number;
    buttonProps?: ButtonProps;
    buttonLabel?: string;
    renderTriggerComponent?: React.ReactNode;
    headerComponentProps?: HeaderComponentProps;
    renderFooterComponent?: React.ReactNode;
    popOverContentProps?: React.ComponentProps<typeof PopoverPrimitive.Content>;
    popOverTriggerProps?: React.ComponentProps<typeof PopoverPrimitive.Trigger>;
  };

const PopOver = ({
  children,
  triggerMode = "click",
  hoverOpenDelay = 0,
  hoverCloseDelay = 100,
  buttonProps,
  buttonLabel,
  renderTriggerComponent,
  renderFooterComponent,
  popOverContentProps,
  popOverTriggerProps,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  ...rest
}: PopOverProps) => {
  const isHoverTrigger = triggerMode === "hover";
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const open = isControlled ? controlledOpen : internalOpen;
  const openTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }

      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  const clearTimers = React.useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleOpen = React.useCallback(() => {
    if (!isHoverTrigger) return;

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    if (hoverOpenDelay <= 0) {
      setOpen(true);
      return;
    }

    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
    }

    openTimeoutRef.current = setTimeout(() => {
      setOpen(true);
      openTimeoutRef.current = null;
    }, hoverOpenDelay);
  }, [hoverOpenDelay, isHoverTrigger, setOpen]);

  const scheduleClose = React.useCallback(() => {
    if (!isHoverTrigger) return;

    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }

    if (hoverCloseDelay <= 0) {
      setOpen(false);
      return;
    }

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, hoverCloseDelay);
  }, [hoverCloseDelay, isHoverTrigger, setOpen]);

  React.useEffect(() => clearTimers, [clearTimers]);

  const handleContentMouseEnter: React.MouseEventHandler<HTMLDivElement> = (event) => {
    popOverContentProps?.onMouseEnter?.(event);
    scheduleOpen();
  };

  const handleContentMouseLeave: React.MouseEventHandler<HTMLDivElement> = (event) => {
    popOverContentProps?.onMouseLeave?.(event);
    scheduleClose();
  };

  const triggerNode = renderTriggerComponent || (
    <Button {...buttonProps}>{buttonLabel}</Button>
  );

  return (
    <ShadPopOver {...rest} open={open} onOpenChange={setOpen}>
      {isHoverTrigger ? (
        <PopoverAnchor
          asChild
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          onFocus={scheduleOpen}
          onBlur={scheduleClose}
        >
          {triggerNode}
        </PopoverAnchor>
      ) : (
        <PopoverTrigger asChild {...popOverTriggerProps}>
          {triggerNode}
        </PopoverTrigger>
      )}
      <PopoverContent
        {...popOverContentProps}
        className={cn("mt-1", popOverContentProps?.className)}
        onMouseEnter={handleContentMouseEnter}
        onMouseLeave={handleContentMouseLeave}
      >
        {children}
        {renderFooterComponent || <></>}
      </PopoverContent>
    </ShadPopOver>
  );
};

export default PopOver;

export { ShadPopOver, PopoverTrigger, PopoverContent, PopoverAnchor };
